import { useMutation, useQuery } from '@tanstack/react-query';
import { ExclamationCircleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { ArrowRight } from 'lucide-react';
import clsx from 'clsx';
import { format, isFuture } from 'date-fns';
import React, { useEffect, useState } from 'react';
import Button from '@dashboard/components/Button';
import Faq from '@dashboard/components/Faq';
import { Label, FormSubmit } from '@dashboard/components/Form';
import { Switch } from '@dashboard/components/ui/switch';
import { FormErrorBase } from '@dashboard/components/Formik/components/FormError';
import AlertDialog from '@dashboard/components/AlertDialog';
import Link from '@dashboard/components/Link';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@dashboard/components/ui/select';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { formatPrice } from '@dashboard/services/formatting';
import plans, { getBillingPeriod, getPlan } from '@dashboard/services/plans';
import { fetcher } from '@dashboard/services/request';
import { getSellerSubdomain } from '@dashboard/services/getSellerDomain';
import { toast } from '@dashboard/context/ToastContext';
import SettingsContainer from '@dashboard/components/SettingsContainer';
import SettingsHeader from '@dashboard/components/SettingsHeader';
import useUrlQuery from '@dashboard/hooks/useUrlQuery';
import { useHistory } from 'react-router-dom';
import ScrollToTop from '@/shared/components/ScrollToTop';
import { sellerKey, sellerUsageKey } from '@dashboard/lib/queryKeys';
import { getSellerUsage } from '@dashboard/hooks/queries/useSellerQueries';
import { Alert, AlertTitle, AlertDescription } from '@dashboard/components/ui/alert';
import { stringify } from 'query-string';
import FreePlanChanges from '@dashboard/components/FreePlanChanges/FreePlanChanges';
import contactSupport from '@dashboard/services/contactSupport';
import SectionHeading from '@dashboard/components/SectionHeading';
import { Badge } from '@dashboard/components/ui/badge';
import getCouponDiscount from './getCouponDiscount';
import useRewardful from '@dashboard/hooks/useRewardful';
import scaleBrackets, { legacyScaleBrackets } from '@dashboard/services/scaleBrackets';
import normalizeSellerPlan from '@dashboard/services/normalizeSellerPlan';

const availablePlans = plans.filter(plan => plan.available);

const planRank = plan => plan.monthlyPrice ?? plan.yearlyPrice ?? 0;

const Plan = ({
  handle,
  version,
  bracket,
  billingPeriod = 'month',
  discount,
  selected,
  setSelectedPlanHandle,
  isCurrentPlan,
  isTrialPlan = false,
  selectedScaleBracket,
  setSelectedScaleBracket,
  isLegacy50k = false,
  isSelectedBracketLegacy = false,
  setIsSelectedBracketLegacy,
}) => {
  const plan = getPlan(handle, version, bracket);

  if (!plan) return null;

  const isScalePlan = handle === 'scale';

  // Include legacy bracket when user is on legacy $50k plan
  const availableScaleBrackets = isLegacy50k
    ? [...scaleBrackets.slice(0, 3), legacyScaleBrackets[0], ...scaleBrackets.slice(3)]
    : scaleBrackets;

  // Find the bracket based on both value and legacy flag
  const scaleBracket = isScalePlan
    ? (isSelectedBracketLegacy
        ? legacyScaleBrackets.find(b => b.value === selectedScaleBracket && b.isLegacy)
        : scaleBrackets.find(b => b.value === selectedScaleBracket)) ||
      scaleBrackets.find(b => b.value === selectedScaleBracket)
    : null;

  // Legacy v1/v2 entries are available: false; skip the bracket-price override
  // so they keep their own plans.js pricing instead of the current $30K bracket.
  const displayPlan =
    isScalePlan && scaleBracket && plan.available
      ? {
          ...plan,
          monthlyPrice: scaleBracket.monthlyPrice,
          yearlyPrice: scaleBracket.yearlyPrice,
          yearlyPriceFull: scaleBracket.yearlyPriceFull,
          benefits: [scaleBracket.label],
        }
      : plan;

  // Bold sales-limit line for available plans; legacy plans keep their benefits line.
  const salesLimit = !displayPlan.available
    ? null
    : plan.handle === 'free' || plan.handle === 'launch'
      ? 'Up to $3K'
      : plan.handle === 'unlimited'
        ? 'Unlimited'
        : isScalePlan && scaleBracket
          ? scaleBracket.label.replace(' sales per month', '')
          : null;

  return (
    <div
      className={clsx(
        'relative w-full rounded-md border p-4 text-left transition-colors',
        'has-[[data-plan-select]:focus-visible]:ring-2 has-[[data-plan-select]:focus-visible]:ring-ring has-[[data-plan-select]:focus-visible]:ring-offset-2',
        isCurrentPlan
          ? 'border-transparent bg-muted cursor-default'
          : selected
            ? 'border-primary cursor-pointer'
            : 'border-input cursor-pointer hover:border-muted-foreground/40',
      )}
    >
      {/* A sibling of the sales limit select: a select inside a button is invalid and steals its taps. */}
      <button
        type="button"
        data-plan-select
        aria-label={displayPlan.title}
        aria-pressed={selected}
        className="absolute inset-0 z-[1] rounded-md outline-none disabled:cursor-default"
        onClick={() => setSelectedPlanHandle(plan.handle)}
        disabled={isCurrentPlan}
      />
      {!isCurrentPlan && (
        <span
          className={clsx(
            'absolute right-3 top-3 flex size-4 items-center justify-center rounded-full border transition-colors',
            selected ? 'border-primary' : 'border-input',
          )}
        >
          {selected && <span className="size-2 rounded-full bg-primary" />}
        </span>
      )}
      <div className="flex justify-between flex-start">
        <div className="text-left flex-1">
          <div className="flex justify-between gap-x-2">
            <div className="font-bold mb-1 text-sm flex items-center gap-x-2">
              <span>
                {displayPlan.title}

                {isCurrentPlan && billingPeriod === 'year' ? ' yearly' : ''}

                {displayPlan.available ? (
                  ''
                ) : (
                  <span className="ml-1 text-gray-700 dark:text-muted-foreground font-normal text-sm">
                    {'('}legacy{')'}
                  </span>
                )}
              </span>

              {isScalePlan && !isCurrentPlan && <Badge variant="success">Recommended</Badge>}

              {isTrialPlan && <Badge variant="secondary">Your trial</Badge>}
            </div>

            {isCurrentPlan && <PlanStatus plan={plan} isCurrentPlan={isCurrentPlan} />}
          </div>

          <div className="text-gray-700 dark:text-muted-foreground font-medium mb-1 whitespace-nowrap text-sm">
            {displayPlan.handle === 'unlimited' && (
              <>
                <span className="text-black dark:text-foreground font-bold">
                  {formatPrice({ amount: displayPlan.yearlyPrice, currency: 'USD' })} / month
                </span>
                <span className="ml-1 text-gray-500 dark:text-muted-foreground font-normal">
                  ({formatPrice({ amount: displayPlan.yearlyPriceFull, currency: 'USD' })} billed
                  annually)
                </span>
              </>
            )}

            {displayPlan.handle === 'free' && (
              <span className="text-black dark:text-foreground font-bold">$0 / month</span>
            )}

            {displayPlan.handle !== 'unlimited' && displayPlan.handle !== 'free' && (
              <>
                {billingPeriod === 'month' && (
                  <>
                    <span className="text-black dark:text-foreground font-bold">
                      {isCurrentPlan && discount
                        ? `${formatPrice({
                            amount:
                              displayPlan.monthlyPrice -
                              getCouponDiscount(displayPlan.monthlyPrice, {
                                amountOff: discount.amountOff,
                                percentOff: discount.percentOff,
                              }),
                            currency: 'USD',
                          })} / month`
                        : `${formatPrice({
                            amount: displayPlan.monthlyPrice,
                            currency: 'USD',
                          })} / month`}
                    </span>{' '}
                  </>
                )}

                {billingPeriod === 'year' && (
                  <>
                    <span className="text-black dark:text-foreground font-bold">
                      <span className="line-through text-gray-600 dark:text-muted-foreground mr-2">
                        {formatPrice({
                          amount: displayPlan.monthlyPrice,
                          currency: 'USD',
                        })}{' '}
                        / month
                      </span>

                      {isCurrentPlan && discount ? (
                        <>
                          {formatPrice({
                            amount:
                              displayPlan.yearlyPrice -
                              getCouponDiscount(displayPlan.yearlyPrice, {
                                amountOff: discount.amountOff,
                                percentOff: discount.percentOff,
                              }),
                            currency: 'USD',
                          })}{' '}
                          / month
                        </>
                      ) : (
                        <>
                          {formatPrice({
                            amount: displayPlan.yearlyPrice,
                            currency: 'USD',
                          })}{' '}
                          / month
                        </>
                      )}
                    </span>
                    {displayPlan.yearlyPriceFull && (
                      <span className="ml-1 text-gray-500 dark:text-muted-foreground font-normal">
                        (
                        {formatPrice({
                          amount: displayPlan.yearlyPriceFull,
                          currency: 'USD',
                        })}{' '}
                        billed annually)
                      </span>
                    )}
                  </>
                )}
              </>
            )}
          </div>

          {isScalePlan && !isCurrentPlan ? (
            <div className="mt-2">
              <SectionHeading>Sales limit</SectionHeading>
              <div className="relative z-[2] text-gray-700 dark:text-muted-foreground font-medium mt-1.5 text-sm w-full max-w-xs">
                <Select
                  value={
                    isSelectedBracketLegacy
                      ? `${selectedScaleBracket}-legacy`
                      : String(selectedScaleBracket)
                  }
                  onValueChange={rawValue => {
                    const isLegacy = rawValue.endsWith('-legacy');
                    const numericValue = isLegacy
                      ? Number(rawValue.replace('-legacy', ''))
                      : Number(rawValue);
                    setSelectedScaleBracket(numericValue);
                    if (setIsSelectedBracketLegacy) {
                      setIsSelectedBracketLegacy(isLegacy);
                    }
                    setSelectedPlanHandle('scale');
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {availableScaleBrackets.map(bracket => (
                      <SelectItem
                        key={bracket.isLegacy ? `${bracket.value}-legacy` : bracket.value}
                        value={bracket.isLegacy ? `${bracket.value}-legacy` : String(bracket.value)}
                      >
                        {bracket.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-sm font-normal text-gray-500 dark:text-muted-foreground mt-1.5">
                Price scales with your monthly sales volume.
              </p>
            </div>
          ) : salesLimit ? (
            <div className="mt-2">
              <SectionHeading>Sales limit</SectionHeading>
              <p className="text-sm font-normal text-gray-700 dark:text-muted-foreground mt-1">
                <span className="font-semibold text-gray-900 dark:text-foreground">
                  {salesLimit}
                </span>
                {salesLimit !== 'Unlimited' && ' in monthly sales'}
              </p>
              {plan.handle === 'unlimited' && (
                <p className="text-sm font-normal text-gray-500 dark:text-muted-foreground mt-1">
                  No sales limit and no overage fees.
                </p>
              )}
            </div>
          ) : (
            <div className="text-gray-700 dark:text-muted-foreground font-medium text-sm">
              {displayPlan.benefits.map((benefit, index) => (
                <span key={index}>
                  {benefit}
                  {index < displayPlan.benefits.length - 1 ? <span className="mx-1">•</span> : ''}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const PlanOverview = () => {
  const history = useHistory();
  const {
    plan: sellerPlan,
    seller,
    isCanceled,
    isTrialing,
    isCanceling,
    isTrialExpired,
    token,
    sellerId,
  } = useAuth();
  const urlQuery = useUrlQuery();
  const {
    billing_period,
    plan: planQuery,
    coupon: couponQuery,
    scaleBracket: scaleBracketQuery,
  } = urlQuery;

  const [selectedBillingPeriod, setSelectedBillingPeriod] = useState(
    billing_period || seller.planBillingPeriod || 'month',
  );

  // Canceling sellers (scheduled cancel or free downgrade) are still on their
  // paid plan, so they fall through to the preselect-current-plan effect below
  // instead of defaulting to Scale.
  const [selectedPlanHandle, setSelectedPlanHandle] = React.useState(
    planQuery ? planQuery : isTrialing || isCanceled || isTrialExpired ? 'scale' : null,
  );

  const [selectedScaleBracket, setSelectedScaleBracket] = useState(
    scaleBracketQuery ? Number(scaleBracketQuery) : sellerPlan.bracket || 10000,
  );

  // Track whether the selected bracket is the legacy version (for $50k customers on old pricing)
  // Initialize based on whether the customer is on legacy $50k plan
  const isLegacy50kCustomer =
    sellerPlan.handle === 'scale' &&
    String(sellerPlan.version) === '3' &&
    sellerPlan.bracket === 50000;

  const [isSelectedBracketLegacy, setIsSelectedBracketLegacy] = useState(isLegacy50kCustomer);

  const cardBillingPeriod = selectedPlanHandle === 'unlimited' ? 'year' : selectedBillingPeriod;

  useEffect(() => {
    if (sellerPlan.bracket) {
      setSelectedScaleBracket(sellerPlan.bracket);
      return;
    }

    if (sellerPlan.handle === 'scale') {
      setSelectedScaleBracket(30000);
      return;
    }
  }, [sellerPlan.bracket, sellerPlan.handle]);

  const { coupon: rewardfulCoupon } = useRewardful();

  const { data: coupon } = useQuery({
    queryKey: ['coupon', couponQuery, rewardfulCoupon, sellerId],
    queryFn: () =>
      fetcher(
        `/sellers/${sellerId}/validate-coupon?coupon=${couponQuery || rewardfulCoupon.id}`,
        token,
      ),
    enabled: !!sellerId && (!!couponQuery || !!rewardfulCoupon),
    retry: false,
  });

  const currentPlan = getPlan(sellerPlan.handle, sellerPlan.version, sellerPlan.bracket) || {};
  const selectedPlan = getPlan(selectedPlanHandle);

  useEffect(() => {
    if (planQuery) {
      setSelectedPlanHandle(planQuery);
    }
  }, [planQuery]);

  useEffect(() => {
    if (!seller._id) return;

    if (selectedPlanHandle) return;

    if (planQuery) return;

    if (seller.subscriptionStatus === 'legacy') return;

    const preselectPlan = availablePlans.find(
      plan => plan.handle === currentPlan.handle && plan.version === currentPlan.version,
    );

    if (preselectPlan) {
      setSelectedPlanHandle(preselectPlan.handle);
    }
  }, [seller, currentPlan]);

  // set billing period from sellerPlan
  useEffect(() => {
    if (billing_period) return;

    if (seller.planBillingPeriod) {
      setSelectedBillingPeriod(seller.planBillingPeriod);

      history.push({
        search: stringify({
          ...urlQuery,
          billing_period: seller.planBillingPeriod,
        }),
      });
    }
  }, [seller]);

  // set coupon from sellerPlan
  useEffect(() => {
    if (couponQuery) return;

    if (seller.stripeSubscriptionDiscount) {
      history.push({
        search: stringify({
          ...urlQuery,
          coupon: seller.stripeSubscriptionDiscount.promotionCode,
        }),
      });
    }
  }, [seller]);

  function toggleBillingPeriod() {
    setSelectedBillingPeriod(selectedBillingPeriod === 'month' ? 'year' : 'month');

    history.push({
      search: stringify({
        ...urlQuery,
        billing_period: selectedBillingPeriod === 'month' ? 'year' : 'month',
      }),
    });
  }

  const hasSubscription = seller.subscriptionStatus === 'active';
  const isFree = seller.subscriptionStatus === 'free';

  const sellerLosesCustomDomain =
    seller.customDomain && seller.customDomainVerified && selectedPlanHandle === 'launch';

  const monthlyCouponDiscount = getCouponDiscount(selectedPlan?.monthlyPrice, {
    amountOff: coupon?.amount_off,
    percentOff: coupon?.percent_off,
  });

  const yearlyCouponDiscount = getCouponDiscount(selectedPlan?.yearlyPrice * 12, {
    amountOff: coupon?.amount_off,
    percentOff: coupon?.percent_off,
  });

  const discountedMonthlyPrice = selectedPlan?.monthlyPrice - monthlyCouponDiscount;

  const yearlyPrice = selectedPlan?.yearlyPrice * 12;
  const discountedYearlyPrice = yearlyPrice - yearlyCouponDiscount;

  return (
    <>
      <ScrollToTop />

      <SettingsContainer size="2xl">
        {currentPlan.handle && (
          <div>
            <div>
              {(hasSubscription || isFree) && (
                <div className="mb-8">
                  <SettingsHeader>Current plan</SettingsHeader>

                  <div className="mt-4">
                    <Plan
                      handle={currentPlan.handle}
                      version={currentPlan.version}
                      bracket={sellerPlan.bracket}
                      billingPeriod={seller.planBillingPeriod}
                      discount={seller.stripeSubscriptionDiscount}
                      setSelectedPlanHandle={() => null}
                      isCurrentPlan
                      selectedScaleBracket={sellerPlan.bracket || 30000}
                      isLegacy50k={isLegacy50kCustomer}
                      isSelectedBracketLegacy={isLegacy50kCustomer}
                    />
                  </div>
                </div>
              )}

              <div className="">
                <SettingsHeader>
                  {isTrialing || isTrialExpired
                    ? 'Start subscription'
                    : seller.subscriptionStatus === 'active'
                      ? 'Change plan'
                      : 'Upgrade plan'}
                </SettingsHeader>

                <p className="text-sm text-gray-600 dark:text-muted-foreground mt-1">
                  Pay a fixed monthly price based on your sales volume, plus Stripe's standard
                  processing fees.
                </p>

                <div className="flex flex-wrap items-center justify-between gap-3 mt-4 mb-3">
                  <div>
                    <Link
                      to="/upgrade"
                      className="whitespace-nowrap text-sm font-medium underline underline-offset-2"
                    >
                      Compare pricing plans <ArrowRight className="inline-block w-3.5 h-3.5" />
                    </Link>
                  </div>

                  {selectedPlanHandle !== 'unlimited' && (
                    <div className="flex items-center">
                      <Switch
                        checked={selectedBillingPeriod === 'year'}
                        onCheckedChange={toggleBillingPeriod}
                        id="billingPeriod"
                      />
                      <Label
                        className="ml-2 cursor-pointer !mb-0 !text-sm font-medium !text-gray-900 dark:!text-foreground"
                        htmlFor="billingPeriod"
                      >
                        Yearly billing{' '}
                        <span className="text-green-700 dark:text-green-500">(2 months free)</span>
                      </Label>
                    </div>
                  )}
                </div>

                <div className="space-y-3">
                  {availablePlans
                    .filter(plan => {
                      if (isTrialing) return true;
                      if (isCanceling) return true;

                      if (selectedBillingPeriod !== seller.planBillingPeriod) return true;

                      return true;
                    })
                    .map(plan => (
                      <Plan
                        key={plan.handle}
                        handle={plan.handle}
                        version={plan.version}
                        billingPeriod={cardBillingPeriod}
                        selected={selectedPlan && selectedPlan.handle === plan.handle}
                        isTrialPlan={isTrialing && currentPlan.handle === plan.handle}
                        setSelectedPlanHandle={setSelectedPlanHandle}
                        selectedScaleBracket={selectedScaleBracket}
                        setSelectedScaleBracket={setSelectedScaleBracket}
                        isLegacy50k={isLegacy50kCustomer}
                        isSelectedBracketLegacy={isSelectedBracketLegacy}
                        setIsSelectedBracketLegacy={setIsSelectedBracketLegacy}
                      />
                    ))}
                </div>

                {sellerLosesCustomDomain && (
                  <div className="mt-4">
                    <Alert variant="warning" className="my-4">
                      <ExclamationCircleIcon />
                      <AlertDescription>
                        You'll lose your custom domain. Subscribe to Scale to keep your custom
                        domain.
                      </AlertDescription>
                    </Alert>
                  </div>
                )}

                {selectedPlanHandle === 'free' && sellerPlan.handle !== 'free' && (
                  <div className="mt-4">
                    <Alert>
                      <InformationCircleIcon />
                      <AlertTitle>Switching to Free means</AlertTitle>
                      <AlertDescription>
                        <p className="text-sm text-gray-600 dark:text-muted-foreground">
                          You keep all your checkout pages, events, forms, customers, and data.
                        </p>
                        <FreePlanChanges seller={seller} showIntro={false} className="mt-1" />
                      </AlertDescription>
                    </Alert>
                  </div>
                )}

                {coupon && (
                  <div className="flex justify-end mt-2">
                    <div className="bg-orange-50 dark:bg-orange-500/15 px-4 py-4 space-y-1 text-right rounded">
                      {rewardfulCoupon && (
                        <p>
                          <span className="font-semibold">{rewardfulCoupon.name}</span>
                        </p>
                      )}

                      <p>
                        <span className="font-semibold">Discount</span>:{' '}
                        {formatPrice({
                          amount:
                            cardBillingPeriod === 'year'
                              ? yearlyCouponDiscount
                              : monthlyCouponDiscount,
                          currency: 'USD',
                        })}
                        <>
                          {coupon.duration === 'once' && ' (one-time)'}

                          {coupon.duration === 'forever' && ` / ${cardBillingPeriod}`}

                          {coupon.duration === 'repeating' &&
                            ` (${coupon.duration_in_months} months)`}
                        </>
                      </p>

                      <p>
                        <span className="font-semibold">Total</span>:{' '}
                        {formatPrice({
                          amount:
                            cardBillingPeriod === 'year'
                              ? discountedYearlyPrice
                              : discountedMonthlyPrice,
                          currency: 'USD',
                        })}{' '}
                        / {cardBillingPeriod}
                      </p>
                    </div>
                  </div>
                )}

                <div className="mt-4">
                  {selectedPlan && (
                    <SubscriptionButton
                      plan={selectedPlan}
                      billingPeriod={cardBillingPeriod}
                      setSelectedPlanHandle={setSelectedPlanHandle}
                      upgradePath="/settings/plan/upgrade"
                      upgradeFlow="billing-settings"
                      selectedScaleBracket={selectedScaleBracket}
                      isSelectedBracketLegacy={isSelectedBracketLegacy}
                      isLegacy50kCustomer={isLegacy50kCustomer}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </SettingsContainer>

      <div className="mt-16">
        <SettingsContainer size="2xl">
          <SettingsHeader>Frequently asked questions</SettingsHeader>

          <div className="mx-auto max-w-2xl">
            <Faq>
              <Faq.Item
                question="What happens when my 7-day free trial ends?"
                answer="You won't lose anything. You can subscribe to a paid plan, or keep going on our Free plan, where your checkout, event, and form pages stay live with no time limit. The Free plan covers up to $3K in sales per month and shows Checkout Page branding on your pages and emails."
              />

              <Faq.Item
                question="Are there any limits to the 7-day free trial?"
                answer="During your 7-day free trial, you can process up to $3K in payments. If you exceed this limit, you'll need to subscribe to a paid plan, or switch to the Free plan, to keep accepting payments.
                <br /><br />
                If you expect to pass $3K during your trial, start a subscription early to avoid any interruption to your payments."
              />

              <Faq.Item
                question="What's included in the Free plan?"
                answer={
                  <>
                    <p className="mb-4">
                      The Free plan has no monthly fee and no transaction fees, just Stripe's
                      standard processing fees, on up to $3K in sales per month. It includes
                      unlimited checkout pages, events, and forms, and one team member.
                    </p>
                    <p>
                      Your pages and emails show Checkout Page branding. Upgrade to a paid plan to
                      remove it and unlock more advanced features.
                    </p>
                  </>
                }
              />

              <Faq.Item
                question="What happens if I exceed the sales per month limit?"
                answer={
                  <>
                    <p className="mb-4">
                      On the Free plan, your pages pause once you pass $3K in sales for the month.
                      They come back at the start of the next month, or as soon as you upgrade.
                    </p>
                    <p className="mb-4">
                      On a paid plan, we'll never stop you from taking payments. We'll let you know
                      as you approach your limit, and you'll be charged an overage fee if you go
                      over it. You can upgrade at any time to increase your sales per month limit.
                    </p>
                    <p>
                      For detailed information about overages,{' '}
                      <Link href="/docs/articles/pricing-fees" target="blank" variant="secondary">
                        visit our pricing guide
                      </Link>
                      .
                    </p>
                  </>
                }
              />

              <Faq.Item
                question="If I don't like Checkout Page, can I request a refund?"
                answer={
                  <>
                    <p className="mb-4">
                      We'd hate to see you go, but we totally understand if Checkout Page is not the
                      right fit for you.
                    </p>
                    <p className="mb-4">
                      If you're not satisfied, we offer a 7-day money-back guarantee for new
                      customers on both monthly and annual plans.
                    </p>
                    <p className="mb-4">
                      You're eligible for a full refund if you contact us within 7 days of starting
                      your subscription, and you haven't processed any revenue through your account.
                    </p>
                    <p className="mb-4">
                      Refer to our{' '}
                      <Link href="/docs/articles/refund-policy" target="blank" variant="secondary">
                        refund policy
                      </Link>{' '}
                      for more information.
                    </p>
                  </>
                }
              />

              <Faq.Item
                question="How do I change my plan?"
                answer="You can change your plan anytime. Upgrades take effect immediately and you'll be charged the difference between the old and new plan. Downgrades take effect at the end of the current billing period."
              />

              <Faq.Item
                question="How do I change my billing period?"
                answer={
                  <>
                    <div className="mb-4">You can change your billing period at any time.</div>
                    <div className="mb-4">
                      When you change from monthly to yearly billing, the change goes into effect
                      immediately.
                    </div>
                    <div className="mb-4">
                      Please contact us to change from yearly to monthly billing. This change goes
                      into effect at the start of the next billing period.
                    </div>
                  </>
                }
              />
            </Faq>
          </div>
        </SettingsContainer>
      </div>

      {hasSubscription && !seller.stripeSubscriptionCancelAt && (
        <div className="mt-16">
          <SettingsContainer size="2xl">
            <SettingsHeader>Cancel subscription</SettingsHeader>
            <div className="mb-4 text-sm">
              You can cancel at any time. You'll keep access until the end of your billing period.
              You can also downgrade to the Free plan instead and keep selling, free up to $3K in
              monthly sales. We don't delete anything: your account, pages, products, events,
              customers and other data will still be there when you resubscribe.
            </div>
            <div className="flex justify-between mt-1.5">
              <Button appearance="secondary" to="/settings/plan/cancel">
                Cancel subscription
              </Button>
            </div>
          </SettingsContainer>
        </div>
      )}

      {seller.subscriptionStatus === 'free' && (
        <div className="mt-16">
          <SettingsContainer size="2xl">
            <SettingsHeader>Cancel Free plan</SettingsHeader>
            <div className="mb-4 text-sm">
              You're on the Free plan and not paying anything. If you cancel, your store goes
              offline immediately and your pages stop accepting payments. We don't delete anything:
              your account, pages, products, events, customers and other data will still be there if
              you come back.
            </div>
            <div className="flex justify-between mt-1.5">
              <Button appearance="secondary" to="/settings/plan/cancel">
                Cancel free plan
              </Button>
            </div>
          </SettingsContainer>
        </div>
      )}
    </>
  );
};

function PlanStatus({ isCurrentPlan }) {
  const { isTrialing, seller, token } = useAuth();

  const hasActiveSubscription = seller.subscriptionStatus === 'active';
  const isFree = seller.subscriptionStatus === 'free';

  const { data: usage } = useQuery({
    queryKey: sellerUsageKey(seller?._id),
    queryFn: () => getSellerUsage(token, seller._id),
    enabled: !!seller?._id && isFree && isCurrentPlan,
    staleTime: 60_000,
  });

  if (!isCurrentPlan || isTrialing) {
    return null;
  }

  if (isFree) {
    if (!usage?.periodEnd) return null;

    const periodEnd = format(new Date(usage.periodEnd), 'd MMM, yyyy');

    return (
      <div className="text-gray-700 dark:text-muted-foreground font-medium text-sm">
        {usage.capReached ? `Paused until ${periodEnd}` : `Sales limit resets on ${periodEnd}`}
      </div>
    );
  }

  if (!hasActiveSubscription) {
    return null;
  }

  return (
    <div className="text-gray-700 dark:text-muted-foreground font-medium text-sm">
      {getRenewalStatus(seller)}
    </div>
  );
}

function getRenewalStatus(seller) {
  const trialEndsAt = seller.trialEnd || null;
  const renewsAt = seller.stripeSubscriptionCurrentPeriodEnd || null;
  const cancelsAt = seller.stripeSubscriptionCancelAt || null;
  const hasPendingPlanChange = seller.pendingPlanChange?.stripeSubscriptionScheduleId;

  // pending plan changes are always downgrades
  if (hasPendingPlanChange) {
    const pendingPlan = seller.pendingPlanChange.plan;
    if (pendingPlan.handle === 'scale' && pendingPlan.bracket) {
      return `Downgrades to ${pendingPlan.title} ($${pendingPlan.bracket / 1000}K) on ${format(
        new Date(seller.pendingPlanChange.effectiveDate),
        'd MMM, yyyy',
      )}`;
    }

    return `Downgrades to ${seller.pendingPlanChange.plan.title} on ${format(
      new Date(seller.pendingPlanChange.effectiveDate),
      'd MMM, yyyy',
    )}`;
  }

  if (cancelsAt && seller.pendingFreeDowngrade) {
    return `Switches to Free on ${format(new Date(cancelsAt), 'd MMM, yyyy')}`;
  }

  if (cancelsAt) {
    return `Cancels on ${format(new Date(cancelsAt), 'd MMM, yyyy')}`;
  }

  if (trialEndsAt && isFuture(new Date(trialEndsAt))) {
    return `Trialing, first payment on ${format(new Date(renewsAt), 'd MMM, yyyy')}`;
  }

  return `Renews on ${format(new Date(renewsAt), 'd MMM, yyyy')}`;
}

export function SubscriptionButton({
  plan = {},
  billingPeriod,
  setSelectedPlanHandle,
  upgradePath = '/settings/plan/upgrade',
  upgradeFlow,
  selectedScaleBracket,
  isSelectedBracketLegacy = false,
  isLegacy50kCustomer = false,
}) {
  const history = useHistory();
  const { seller, sellerId, plan: sellerPlan, token, setSeller } = useAuth();
  const [error, setError] = useState(null);
  const [showDowngradeDialog, setShowDowngradeDialog] = useState(false);
  const { coupon: couponQuery } = useUrlQuery();

  const effectiveBillingPeriod = plan.monthlyPrice === null ? 'year' : billingPeriod;

  const sellerLosesCustomDomain =
    seller.customDomain && seller.customDomainVerified && plan.handle === 'launch';

  const pendingPlan = seller.pendingPlanChange?.plan?.handle
    ? getPlan(seller.pendingPlanChange?.plan?.handle, seller.pendingPlanChange?.plan?.version)
    : null;

  const currentPlan =
    pendingPlan || getPlan(sellerPlan.handle, sellerPlan.version, sellerPlan.bracket) || {};

  const { data: updatedSeller, refetch: refetchSeller } = useQuery({
    queryKey: sellerKey(sellerId),
    queryFn: () => fetcher(`/sellers/${sellerId}`, token),
  });

  const changePlanMutation = useMutation({
    mutationFn: () =>
      fetcher(`/sellers/${sellerId}/change-subscription`, token, {
        method: 'POST',
        body: JSON.stringify({
          handle: plan.handle,
          billingPeriod: effectiveBillingPeriod,
          coupon: couponQuery,
          upgradeFlow,
          ...(selectedScaleBracket && plan.handle === 'scale' && { bracket: selectedScaleBracket }),
        }),
      }),
    onSuccess: async () => {
      const { data: updatedSeller } = await refetchSeller();

      if (updatedSeller.pendingPlanChange) {
        toast({
          title: 'Subscription updated',
          description: `Your subscription will downgrade to ${
            updatedSeller.pendingPlanChange.plan.title
          } on ${format(new Date(updatedSeller.pendingPlanChange.effectiveDate), 'd MMM, yyyy')}`,
          type: 'success',
        });
      } else {
        toast({
          title: 'Subscription updated',
          description: `You're now on the ${plan.title} plan`,
          type: 'success',
        });
      }

      setSelectedPlanHandle(null);
    },
    onError: error => {
      setError(error.message);
    },
  });

  useEffect(() => {
    if (!updatedSeller) return;

    setSeller(updatedSeller);
  }, [updatedSeller]);

  useEffect(() => {
    if (!updatedSeller) return;

    // Sync the selection when the seller's plan actually changed. The raw
    // seller doc can still say `grow`; compare and set the normalized handle,
    // or the selection is reset on every render for those sellers.
    const updatedPlanHandle = normalizeSellerPlan(updatedSeller.plan || {})?.handle;

    if (updatedPlanHandle && updatedPlanHandle !== currentPlan.handle) {
      setSelectedPlanHandle(updatedPlanHandle);
    }
  }, [currentPlan, updatedSeller]);

  const isLegacyPremium =
    seller.subscriptionStatus === 'legacy' && currentPlan.handle === 'premium';

  const canStartSubscription =
    seller.subscriptionStatus === 'free' ||
    seller.subscriptionStatus === 'legacy' ||
    // A comped seller has no Stripe subscription, so converting to paying goes
    // through the normal upgrade flow.
    seller.subscriptionStatus === 'comped' ||
    seller.subscriptionStatus === 'trialing' ||
    seller.subscriptionStatus === 'canceled' ||
    seller.subscriptionStatus === 'trial_expired' ||
    seller.stripeSubscriptionCancelAt;

  if (plan.handle === 'free') {
    if (sellerPlan.handle === 'free') {
      return null;
    }

    const freeLabel = seller.subscriptionStatus === 'active' ? 'Switch to Free' : 'Start for free';
    const freeUrl = `${upgradePath}?${stringify({
      plan: 'free',
      billing_period: 'month',
      upgradeFlow,
    })}`;

    return (
      <Button appearance="primary" size="full" onClick={() => history.push(freeUrl)}>
        {freeLabel}
      </Button>
    );
  }

  if (canStartSubscription) {
    const upgradeUrl = `${upgradePath}?${stringify({
      plan: plan.handle,
      billing_period: effectiveBillingPeriod,
      coupon: couponQuery,
      upgradeFlow,
      ...(selectedScaleBracket &&
        plan.handle === 'scale' && { scaleBracket: selectedScaleBracket }),
    })}`;

    return (
      <>
        <Button
          appearance="primary"
          size="full"
          onClick={() => {
            if (sellerLosesCustomDomain) {
              setShowDowngradeDialog(true);
            } else {
              history.push(upgradeUrl);
            }
          }}
        >
          <a className="btn btn-primary">Start subscription</a>
        </Button>

        <AlertDialog
          isOpen={showDowngradeDialog}
          title="Custom domain not included in Launch"
          options={{
            cancel: 'Choose a different plan',
            confirm: 'Continue with Launch',
          }}
          customContent={
            <div className="space-y-3 text-sm text-gray-700 dark:text-muted-foreground">
              <p>You currently have a custom domain connected:</p>
              <p className="font-medium text-gray-900 dark:text-foreground">
                {seller.customDomain}
              </p>
              <p>The Launch plan does not include custom domains.</p>
              <p>
                If you subscribe to Launch, this domain will be removed and links using it will stop
                working.
              </p>
              <p>Your pages will remain available on:</p>
              <p className="font-medium text-gray-900 dark:text-foreground">
                {getSellerSubdomain(seller).replace('https://', '')}
              </p>
            </div>
          }
          onProceed={() => {
            setShowDowngradeDialog(false);
            history.push(upgradeUrl);
          }}
          onCancel={() => setShowDowngradeDialog(false)}
          onDismiss={() => setShowDowngradeDialog(false)}
        />
      </>
    );
  }

  const hasSubscription = seller.subscriptionStatus === 'active' || isLegacyPremium;

  if (hasSubscription) {
    if (seller.planBillingPeriod !== billingPeriod) {
      const canUpgrade = seller.planBillingPeriod === 'month';

      if (canUpgrade) {
        return (
          <div>
            <FormSubmit.Button
              size="full"
              appearance="primary"
              isSubmitting={changePlanMutation.isLoading}
              hasError={!!error}
              onClick={async () => {
                setError(null);
                changePlanMutation.mutate();
              }}
              disabled={changePlanMutation.isLoading}
            >
              {plan.handle === currentPlan.handle
                ? 'Upgrade to yearly billing'
                : planRank(plan) > planRank(currentPlan)
                  ? `Upgrade to ${plan.title}`
                  : `Change to ${plan.title}`}
            </FormSubmit.Button>

            {error && (
              <div className="mt-2">
                <FormErrorBase>{error}</FormErrorBase>
              </div>
            )}
          </div>
        );
      }

      return (
        <div className="flex flex-wrap gap-2">
          <Button
            size="full"
            onClick={() =>
              contactSupport(
                `Hi! I want to change from ${currentPlan.title} - ${getBillingPeriod(
                  seller.planBillingPeriod,
                )} to ${plan.title} - ${getBillingPeriod(billingPeriod)}.`,
              )
            }
            appearance="secondary"
          >
            Contact us to change to monthly
          </Button>
        </div>
      );
    } else {
      const isSamePlan = currentPlan.handle === plan.handle;
      const isVersionChange = currentPlan.version !== plan.version;

      let isBracketChange = false;
      if (sellerPlan.handle === 'scale') {
        if (pendingPlan?.handle === 'scale') {
          isBracketChange = selectedScaleBracket !== seller.pendingPlanChange?.plan?.bracket;
        } else {
          isBracketChange = selectedScaleBracket !== sellerPlan?.bracket;
        }
        // Also detect when legacy $50k customer switches to new (non-legacy) $50k pricing
        // The bracket value is the same (50000) but the pricing is different
        if (!isBracketChange && isLegacy50kCustomer && !isSelectedBracketLegacy) {
          isBracketChange = true;
        }
      }

      if (isSamePlan && isVersionChange) {
        return (
          <div>
            <FormSubmit.Button
              size="full"
              appearance="primary"
              isSubmitting={changePlanMutation.isLoading}
              hasError={!!error}
              onClick={async () => {
                setError(null);
                changePlanMutation.mutate();
              }}
              disabled={changePlanMutation.isLoading}
            >
              Change to {plan.title}
            </FormSubmit.Button>

            {error && (
              <div className="mt-2">
                <FormErrorBase>{error}</FormErrorBase>
              </div>
            )}
          </div>
        );
      }

      if (plan.handle === 'scale' && isSamePlan && isBracketChange) {
        // Determine button text based on whether this is a legacy-to-new pricing change or a bracket change
        const isLegacyToNewPricingChange =
          isLegacy50kCustomer &&
          !isSelectedBracketLegacy &&
          selectedScaleBracket === sellerPlan?.bracket;
        let buttonText;
        if (isLegacyToNewPricingChange) {
          buttonText = 'Switch to new pricing';
        } else if (!sellerPlan?.bracket || selectedScaleBracket > sellerPlan?.bracket) {
          buttonText = `Change limit to ${formatPrice({
            amount: selectedScaleBracket * 100,
            currency: 'USD',
          })}`;
        } else {
          buttonText = `Change limit to ${formatPrice({
            amount: selectedScaleBracket * 100,
            currency: 'USD',
          })}`;
        }

        return (
          <div>
            <FormSubmit.Button
              size="full"
              appearance="primary"
              isSubmitting={changePlanMutation.isLoading}
              hasError={!!error}
              onClick={async () => {
                setError(null);
                changePlanMutation.mutate();
              }}
              disabled={changePlanMutation.isLoading}
            >
              {buttonText}
            </FormSubmit.Button>

            {error && (
              <div className="mt-2">
                <FormErrorBase>{error}</FormErrorBase>
              </div>
            )}
          </div>
        );
      }

      const isDowngrade = planRank(plan) < planRank(currentPlan);

      if (isDowngrade) {
        return (
          <div>
            <FormSubmit.Button
              size="full"
              appearance="secondary"
              isSubmitting={changePlanMutation.isLoading}
              hasError={!!error}
              onClick={async () => {
                setError(null);
                if (sellerLosesCustomDomain) {
                  setShowDowngradeDialog(true);
                } else {
                  changePlanMutation.mutate();
                }
              }}
              disabled={changePlanMutation.isLoading}
            >
              Downgrade to {plan.title}
            </FormSubmit.Button>

            {error && (
              <div className="mt-2">
                <FormErrorBase>{error}</FormErrorBase>
              </div>
            )}

            <div className="mt-2 text-center text-xs text-gray-500 dark:text-muted-foreground">
              Downgrades take effect at the end of your current billing period
            </div>

            <AlertDialog
              isOpen={showDowngradeDialog}
              title="Custom domain not included in Launch"
              options={{
                cancel: 'Switch to Launch',
                confirm: 'Keep Scale plan',
              }}
              customContent={
                <div className="space-y-3 text-sm text-gray-700 dark:text-muted-foreground">
                  <p>You currently have a custom domain connected:</p>
                  <p className="font-medium text-gray-900 dark:text-foreground">
                    {seller.customDomain}
                  </p>
                  <p>The Launch plan does not include custom domains.</p>
                  <p>
                    If you switch to Launch, this domain will be removed and links using it will
                    stop working.
                  </p>
                  <p>Your pages will remain available on:</p>
                  <p className="font-medium text-gray-900 dark:text-foreground">
                    {getSellerSubdomain(seller).replace('https://', '')}
                  </p>
                </div>
              }
              onProceed={() => setShowDowngradeDialog(false)}
              onCancel={() => {
                setShowDowngradeDialog(false);
                changePlanMutation.mutate();
              }}
              onDismiss={() => setShowDowngradeDialog(false)}
            />
          </div>
        );
      }

      if (isSamePlan) {
        return null;
      }

      return (
        <div>
          <FormSubmit.Button
            size="full"
            appearance="primary"
            isSubmitting={changePlanMutation.isLoading}
            hasError={!!error}
            onClick={async () => {
              setError(null);
              changePlanMutation.mutate();
            }}
            disabled={changePlanMutation.isLoading}
          >
            Upgrade to {plan.title}
          </FormSubmit.Button>

          {error && (
            <div className="mt-2">
              <FormErrorBase>{error}</FormErrorBase>
            </div>
          )}
        </div>
      );
    }
  }

  return '';
}

export default PlanOverview;
